import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Video, Settings, LogOut, Copy, Check, Users, Phone, ArrowRight, Share2, MessageSquareText, Crown, MessageCircle, Mail, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [channelName, setChannelName] = useState('');
  const [showCallCard, setShowCallCard] = useState(false);
  const [copied, setCopied] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  const generateChannelName = () => {
    return 'call-' + Date.now().toString().slice(-10) + '-' + Math.random().toString(36).substring(2, 7);
  };

  const handleCreateCall = () => {
    const name = generateChannelName();
    setChannelName(name);
    setShowCallCard(true);
  };

  const handleCopyLink = async () => {
    const callLink = `${window.location.origin}/call/${channelName}`;
    try {
      await navigator.clipboard.writeText(callLink);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Fallback for browsers/iframes where clipboard is blocked
      try {
        const ta = document.createElement('textarea');
        ta.value = callLink;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopied(true);
        toast.success('Link copied!');
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        toast.error('Copy blocked — please copy the link manually');
      }
    }
  };

  const handleShareLink = async () => {
    const callLink = `${window.location.origin}/call/${channelName}`;
    if (navigator.share) {
      try { await navigator.share({ title: 'Join my Kaplie call', text: 'Tap to join my video call with live captions', url: callLink }); return; }
      catch (e) { if (e.name === 'AbortError') return; }
    }
    await handleCopyLink();
  };

  const handleEmailInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail) return;
    setEmailSending(true);
    try {
      const token = localStorage.getItem('kaplie_token');
      const callLink = `${window.location.origin}/call/${channelName}`;
      await axios.post(
        `${API}/calls/email-invite`,
        { recipient_email: inviteEmail, call_link: callLink },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invite');
    } finally {
      setEmailSending(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('kaplie_token');
    localStorage.removeItem('kaplie_user');
    setUser(null);
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white font-inter">
      {/* Ambient glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-[#D4AF37]/[0.03] rounded-full blur-[150px]" />
      </div>

      {/* Nav */}
      <nav className="relative z-10 border-b border-white/[0.06] backdrop-blur-2xl bg-black/60 sticky top-0">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center">
              <Video className="w-4 h-4 text-black" />
            </div>
            <span className="font-satoshi font-bold text-lg tracking-tight">Kaplie</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-zinc-500 text-sm hidden sm:block mr-2">{user?.name}</span>
            {user?.is_admin && (
              <Link to="/admin">
                <button className="p-2 hover:bg-white/[0.06] rounded-lg transition-colors" data-testid="admin-link">
                  <Crown className="w-4 h-4 text-[#D4AF37]" />
                </button>
              </Link>
            )}
            <Link to="/settings">
              <button className="p-2 hover:bg-white/[0.06] rounded-lg transition-colors" data-testid="settings-link">
                <Settings className="w-4 h-4 text-zinc-500" />
              </button>
            </Link>
            <button onClick={handleLogout} className="p-2 hover:bg-white/[0.06] rounded-lg transition-colors" data-testid="logout-button">
              <LogOut className="w-4 h-4 text-zinc-500" />
            </button>
          </div>
        </div>
      </nav>

      <main className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 py-10">
        {/* Welcome */}
        <div className="mb-10">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D4AF37]/80 mb-2">Dashboard</p>
          <h1 className="font-satoshi font-bold text-3xl tracking-tight">Welcome back, {user?.name?.split(' ')[0]}</h1>
        </div>

        {/* Create Call — Centerpiece */}
        <div className="mb-8">
          {!showCallCard ? (
            <button
              onClick={handleCreateCall}
              data-testid="create-call-button"
              className="kaplie-pulse w-full group relative p-8 md:p-10 rounded-2xl bg-gradient-to-br from-[#D4AF37]/[0.08] to-transparent border border-[#D4AF37]/20 hover:border-[#D4AF37]/40 transition-all duration-500 overflow-hidden"
            >
              {/* Animated gold halo */}
              <span className="kaplie-pulse-halo pointer-events-none absolute inset-0 rounded-2xl" aria-hidden="true" />
              {/* Shimmer streak on hover */}
              <span className="kaplie-shimmer pointer-events-none absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" aria-hidden="true" />
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-5">
                  <div className="kaplie-icon-glow w-14 h-14 rounded-2xl bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center shadow-[0_0_20px_rgba(212,175,55,0.2)] group-hover:shadow-[0_0_30px_rgba(212,175,55,0.5)] transition-shadow duration-500">
                    <Video className="w-7 h-7 text-black" />
                  </div>
                  <div className="text-left">
                    <h2 className="font-satoshi font-bold text-xl mb-1">Create New Call</h2>
                    <p className="text-zinc-500 text-sm">Start a video call with live captions</p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-[#D4AF37] group-hover:translate-x-1 transition-transform" />
              </div>
            </button>
          ) : (
            <div className="p-8 md:p-10 rounded-2xl backdrop-blur-2xl bg-black/60 border border-white/[0.08] shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center">
                  <Video className="w-5 h-5 text-black" />
                </div>
                <h2 className="font-satoshi font-bold text-xl">Your call is ready</h2>
              </div>
              
              <div className="space-y-4">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={`${window.location.host}/call/${channelName}`}
                    readOnly
                    data-testid="call-link-input"
                    className="flex-1 bg-black/40 border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-zinc-300 focus:outline-none"
                  />
                  <button onClick={handleCopyLink} data-testid="copy-call-link" className="px-4 py-3 bg-white/[0.06] hover:bg-white/[0.1] rounded-xl transition-colors">
                    {copied ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5 text-zinc-400" />}
                  </button>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => navigate(`/call/${channelName}`)} data-testid="join-call-btn" className="flex-1 py-3 bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black font-semibold rounded-xl hover:shadow-[0_0_20px_rgba(212,175,55,0.3)] transition-all flex items-center justify-center gap-2">
                    Join Call <ArrowRight className="w-4 h-4" />
                  </button>
                  <button onClick={handleShareLink} data-testid="share-call-link-dashboard" className="px-5 py-3 bg-white/[0.06] hover:bg-white/[0.1] rounded-xl transition-colors flex items-center gap-2 text-sm font-medium">
                    <Share2 className="w-4 h-4" /> Share
                  </button>
                </div>

                {/* Email invite */}
                <div className="pt-4 mt-2 border-t border-white/[0.06]">
                  <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D4AF37]/80 mb-3 flex items-center gap-2">
                    <Mail className="w-3.5 h-3.5" /> Email an invite
                  </p>
                  <form onSubmit={handleEmailInvite} className="flex gap-2">
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="friend@example.com"
                      required
                      data-testid="email-invite-input"
                      className="flex-1 bg-black/40 border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-[#D4AF37]/50 focus:ring-1 focus:ring-[#D4AF37]/30 focus:outline-none transition-all"
                    />
                    <button
                      type="submit"
                      disabled={emailSending || !inviteEmail}
                      data-testid="send-email-invite-btn"
                      className="px-5 py-3 bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black font-semibold rounded-xl hover:shadow-[0_0_20px_rgba(212,175,55,0.3)] transition-all flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {emailSending ? (
                        <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      ) : (
                        <><Send className="w-4 h-4" /> Send</>
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Messages */}
          <Link to="/messages" className="group">
            <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:border-violet-500/20 hover:shadow-[0_0_30px_rgba(138,43,226,0.05)] transition-all duration-300 h-full">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-violet-400" />
                </div>
                <h3 className="font-satoshi font-bold">Messages</h3>
              </div>
              <p className="text-sm text-zinc-500">Direct message other users</p>
            </div>
          </Link>

          {/* Saved Conversations */}
          <Link to="/conversations" className="group">
            <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:border-[#D4AF37]/20 hover:shadow-[0_0_30px_rgba(212,175,55,0.05)] transition-all duration-300 h-full">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                  <MessageSquareText className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <h3 className="font-satoshi font-bold">Saved Conversations</h3>
              </div>
              <p className="text-sm text-zinc-500">Transcripts & AI summaries</p>
            </div>
          </Link>

          {/* Contacts */}
          <Link to="/contacts" className="group">
            <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:border-blue-500/20 hover:shadow-[0_0_30px_rgba(0,229,255,0.05)] transition-all duration-300 h-full">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-400" />
                </div>
                <h3 className="font-satoshi font-bold">Contacts</h3>
              </div>
              <p className="text-sm text-zinc-500">Manage your contact list</p>
            </div>
          </Link>

          {/* Call History */}
          <Link to="/call-history" className="group">
            <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:border-white/[0.12] transition-all duration-300 h-full">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-white/[0.06] flex items-center justify-center">
                  <Phone className="w-5 h-5 text-zinc-400" />
                </div>
                <h3 className="font-satoshi font-bold">Call History</h3>
              </div>
              <p className="text-sm text-zinc-500">View past calls</p>
            </div>
          </Link>
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
